// Mascota Búho-JASQ — estilo flat / vector caricaturesco (referencia: PNG aportado por el usuario).
// Cuerpo café marrón, vientre amarillo con rayas diagonales, ojos enormes turquesa,
// orejas/penachos amarillos triangulares, pico naranja diamante, alas laterales.
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

const PALETTE = {
  info:    { bg: "#FFF7DA", ring: "#F5B82E", text: "#1F2547", emoji: "💡" },
  success: { bg: "#E7F4E9", ring: "#3FA84D", text: "#1F2547", emoji: "⭐" },
  warn:    { bg: "#FFE9F0", ring: "#D63384", text: "#1F2547", emoji: "✋" },
  hint:    { bg: "#E8EEFB", ring: "#2C49A0", text: "#1F2547", emoji: "🦉" },
  party:   { bg: "#F3E8FF", ring: "#A43790", text: "#1F2547", emoji: "🎉" },
};

export function Mascot({
  message,
  variant = "info",
  size = 96,
  onClose,
  showClose = false,
  testid = "owl-bubble",
  className = "",
}) {
  const p = PALETTE[variant] || PALETTE.info;
  return (
    <div className={`flex items-end gap-3 ${className}`} data-testid={testid}>
      <OwlSvg size={size} />
      <AnimatePresence mode="wait">
        <motion.div
          key={message}
          initial={{ opacity: 0, y: 12, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 220, damping: 18 }}
          className="relative max-w-md rounded-3xl border-[3px] px-5 py-4 text-base font-bold shadow-lg"
          style={{ background: p.bg, borderColor: p.ring, color: p.text }}
        >
          <span
            className="absolute -left-2.5 bottom-4 w-4 h-4 rotate-45 border-l-[3px] border-b-[3px]"
            style={{ background: p.bg, borderColor: p.ring }}
          />
          <p className="leading-snug whitespace-pre-line" data-testid={`${testid}-text`}>
            <span className="mr-1.5">{p.emoji}</span>{message}
          </p>
          {showClose && (
            <button data-testid={`${testid}-close`} onClick={onClose}
              className="absolute top-1 right-2 text-[10px] font-bold uppercase tracking-wider opacity-60 hover:opacity-100">
              cerrar
            </button>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// Colores oficiales del búho (extraídos del PNG de referencia)
const BROWN_DARK  = "#7A4A26";
const BROWN_BASE  = "#A06A3A";
const BROWN_LIGHT = "#BC8A57";
const YELLOW      = "#FFCC28";
const YELLOW_DARK = "#F5B82E";
const ORANGE      = "#F26A2E";
const TURQUOISE   = "#74D0CD";
const PUPIL       = "#1F2547";

function OwlSvg({ size = 120 }) {
  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 240 240"
      data-testid="owl-svg"
      className="shrink-0"
      initial={{ y: 0 }}
      animate={{ y: [0, -4, 0, -4, 0] }}
      transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
    >
      <defs>
        <pattern id="bellyStripes" patternUnits="userSpaceOnUse" width="14" height="14" patternTransform="rotate(45)">
          <rect width="14" height="14" fill={YELLOW} />
          <rect width="6" height="14" fill={YELLOW_DARK} opacity="0.5" />
        </pattern>
        <clipPath id="bellyClip">
          <ellipse cx="120" cy="160" rx="55" ry="60" />
        </clipPath>
      </defs>

      {/* === Orejas / penachos amarillos triangulares === */}
      <g>
        <path d="M65 60 L 78 12 L 96 70 Z" fill={YELLOW} />
        <path d="M175 60 L 162 12 L 144 70 Z" fill={YELLOW} />
      </g>

      {/* === Cuerpo principal (forma de pera redondeada) === */}
      <motion.g
        animate={{ scaleY: [1, 1.02, 1] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "120px 130px" }}
      >
        <path
          d="M120 50
             C 70 50, 45 95, 45 145
             C 45 195, 75 222, 120 222
             C 165 222, 195 195, 195 145
             C 195 95, 170 50, 120 50 Z"
          fill={BROWN_BASE}
        />

        {/* Cara/disco facial más claro */}
        <path
          d="M120 65
             C 80 65, 60 95, 60 130
             C 60 158, 80 175, 120 175
             C 160 175, 180 158, 180 130
             C 180 95, 160 65, 120 65 Z"
          fill={BROWN_LIGHT}
        />

        {/* === Ala izquierda (más clara, levantada) === */}
        <motion.path
          d="M48 115
             C 18 130, 18 180, 60 195
             C 75 185, 80 160, 78 130 Z"
          fill={YELLOW_DARK}
          animate={{ rotate: [-2, 4, -2] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "60px 150px" }}
        />
        {/* === Ala derecha (más oscura, baja) === */}
        <motion.path
          d="M192 130
             C 220 145, 220 200, 195 215
             C 178 210, 168 175, 168 140 Z"
          fill={BROWN_DARK}
          animate={{ rotate: [2, -3, 2] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "190px 165px" }}
        />

        {/* === Vientre amarillo con rayas diagonales === */}
        <ellipse cx="120" cy="160" rx="55" ry="60" fill="url(#bellyStripes)" clipPath="url(#bellyClip)" />

        {/* === Patitas café simples === */}
        <g fill={BROWN_DARK}>
          <ellipse cx="100" cy="223" rx="16" ry="6" />
          <ellipse cx="140" cy="223" rx="16" ry="6" />
        </g>
      </motion.g>

      {/* === OJOS GIGANTES ESTILO PNG: blanco + iris turquesa + pupila negra grande + brillo === */}
      <g>
        {/* Ojo izquierdo */}
        <circle cx="92" cy="118" r="32" fill="#FFFFFF" />
        {/* iris turquesa (anillo, no relleno completo) */}
        <circle cx="92" cy="118" r="22" fill="none" stroke={TURQUOISE} strokeWidth="6" />
        {/* pupila grande */}
        <motion.circle
          cx="92" cy="118" r="14" fill={PUPIL}
          initial={{ cx: 90, cy: 118 }}
          animate={{ cx: [90, 94, 90], cy: [118, 119, 118] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* brillo grande blanco */}
        <motion.circle
          cx="98" cy="113" r="4.5" fill="#FFFFFF"
          initial={{ cx: 96 }}
          animate={{ cx: [96, 100, 96] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* parpadeo */}
        <motion.path
          d="M62 118 Q 92 118 122 118"
          stroke={BROWN_DARK} strokeWidth="0" fill="none" strokeLinecap="round"
          animate={{ strokeWidth: [0, 0, 6, 0, 0] }}
          transition={{ duration: 5.5, times: [0, 0.88, 0.93, 0.97, 1], repeat: Infinity }}
        />

        {/* Ojo derecho */}
        <circle cx="148" cy="118" r="32" fill="#FFFFFF" />
        <circle cx="148" cy="118" r="22" fill="none" stroke={TURQUOISE} strokeWidth="6" />
        <motion.circle
          cx="148" cy="118" r="14" fill={PUPIL}
          initial={{ cx: 146, cy: 118 }}
          animate={{ cx: [146, 150, 146], cy: [118, 119, 118] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.circle
          cx="154" cy="113" r="4.5" fill="#FFFFFF"
          initial={{ cx: 152 }}
          animate={{ cx: [152, 156, 152] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.path
          d="M118 118 Q 148 118 178 118"
          stroke={BROWN_DARK} strokeWidth="0" fill="none" strokeLinecap="round"
          animate={{ strokeWidth: [0, 0, 6, 0, 0] }}
          transition={{ duration: 5.5, times: [0, 0.88, 0.93, 0.97, 1], repeat: Infinity }}
        />
      </g>

      {/* === Pico naranja en forma de diamante === */}
      <path d="M120 148 L 110 158 L 120 170 L 130 158 Z" fill={ORANGE} />
    </motion.svg>
  );
}

// Compatibilidad con código existente
export const OwlMascot = Mascot;

export function FloatingOwl({ message, variant = "info", duration = 5000, onDone }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!duration) return;
    const t = setTimeout(() => { setVisible(false); onDone?.(); }, duration);
    return () => clearTimeout(t);
  }, [duration, onDone]);
  if (!visible) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 30 }}
      className="fixed bottom-6 right-6 z-50"
      data-testid="floating-mascot"
    >
      <Mascot message={message} variant={variant} size={84} />
    </motion.div>
  );
}

export default Mascot;
